import React, { useState } from 'react';
import { ApplicationDetails } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import Tag from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/TagsSection/components/Tag';
import LoadingSpinner from 'components/LoadingSpinner';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import { Tag as TagType } from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/TagsSection';
import { OperationType } from 'components/RecordsListView/constants';
import useDetailsTabData, { Applicator } from './hooks/useDetailsTabData';
import ApplicatorsTable from './components/ApplicatorsTable';
import './DetailsTab.css';

export interface DetailsTabExtraField {
  key: string;
  label: string;
  value: string;
}

interface DetailsTabProps {
  applicationDetails: ApplicationDetails;
  type?: OperationType;
  extraFields?: DetailsTabExtraField[];
  hiddenFieldKeys?: string[];
  singleColumn?: boolean;
  contentMaxHeight?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DetailsTab = ({
  applicationDetails,
  type = OperationType.APPLICATION,
  extraFields = [],
  hiddenFieldKeys = [],
  singleColumn = false,
  contentMaxHeight
}: DetailsTabProps) => {
  const { data, isLoading } = useDetailsTabData(applicationDetails, type);
  const [visibleTooltipKey, setVisibleTooltipKey] = useState('');

  const dataList = [
    ...extraFields,
    ...data.filter(item => item.key !== 'applicators' || item.value === '---')
  ].filter(item => !hiddenFieldKeys.includes(item.key)) as typeof data;
  const applicators = data.find(item => item.key === 'applicators')
    ?.value as Applicator[];

  if (isLoading)
    return (
      <div data-testid="loading-spinner">
        <LoadingSpinner message="" />
      </div>
    );

  const tagsItem = dataList.find(item => item.key === 'tags');
  const columnItems = dataList.filter(item => item.key !== 'tags');
  const leftItems = singleColumn
    ? columnItems
    : columnItems.filter((_, i) => i % 2 === 0);
  const rightItems = singleColumn
    ? []
    : columnItems.filter((_, i) => i % 2 === 1);

  const renderField = ({
    key,
    label,
    value,
    tooltip
  }: typeof dataList[number]) => (
    <div
      key={key}
      className="details-field-row flex items-start gap-5 py-2 border-b border-neutral-20"
    >
      <div className="details-field-label text-sm text-neutral-60">
        <div
          className="flex items-center w-fit"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(tooltip && {
            role: 'button',
            tabIndex: 0,
            onMouseEnter: () => setVisibleTooltipKey(key),
            onMouseLeave: () => setVisibleTooltipKey('')
          })}
        >
          <div
            id={`${key}-label`}
            className={tooltip ? 'pr-2' : ''}
            data-testid={`${key}-label`}
          >
            {label}
          </div>
          {tooltip && (
            <Tooltip
              title={tooltip}
              color="#232630"
              open={visibleTooltipKey === key}
              data-testid={`${key}-tooltip-container`}
              overlayInnerStyle={{
                width: 'max-content'
              }}
            >
              <InfoCircleOutlined
                className="!text-blue-60"
                data-testid={`${key}-tooltip`}
                aria-hidden="true"
              />
            </Tooltip>
          )}
        </div>
      </div>
      {key === 'tags' && Array.isArray(value) ? (
        <div className="flex flex-wrap gap-2 flex-1 min-w-0">
          {(value as TagType[])?.map(tag => (
            <Tag
              key={tag?.id}
              name={tag?.name}
              backgroundColor={tag?.backgroundColor}
              textColor={tag?.textColor}
              fontSize="12px"
              paddingRight="8px"
              paddingLeft="8px"
            />
          ))}
        </div>
      ) : (
        <div
          data-testid={`${key}-value`}
          className={`text-sm text-neutral-1000${
            key === 'recordSource'
              ? ' flex-1 overflow-hidden min-w-0 whitespace-nowrap'
              : ''
          }`}
        >
          {value as string}
        </div>
      )}
    </div>
  );

  return (
    <div
      className="details-tab-container"
      style={{
        maxHeight:
          contentMaxHeight ??
          (type === OperationType.WORK_ORDER
            ? 'calc(100vh - 331px)'
            : 'calc(100vh - 373px)'),
        overflowY: 'auto',
        clipPath: 'inset(0 round 8px)'
      }}
    >
      <div
        data-testid="details-tab"
        className="px-6 py-8"
        style={{ paddingBottom: '72px' }}
      >
        <div className="flex flex-col gap-8">
          <div className="details-field-list">
            <div className="details-columns-row">
              <div className="details-left-col">
                {leftItems.map(renderField)}
              </div>
              {!singleColumn && (
                <div className="details-right-col">
                  {rightItems.map(renderField)}
                </div>
              )}
            </div>
            {tagsItem && renderField(tagsItem)}
          </div>

          {/* Applicators render as their own table rather than a field row, so
              hiding them needs an explicit guard as well as the row filter. */}
          {Array.isArray(applicators) &&
            !hiddenFieldKeys.includes('applicators') && (
              <ApplicatorsTable applicators={applicators} />
            )}
        </div>
      </div>
    </div>
  );
};

export default DetailsTab;
